import React, { createContext, useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/utils/cn';

import { ErrorBoundary } from './ErrorBoundary';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface DropdownItemConfig {
    title: string;
    url: string;
    /** As on link tabs: the panel drops its padding so the page can pad itself. */
    noPadding?: boolean;
}

export const pickSearchParams = (search: string, keys: readonly string[]): string => {
    const current = new URLSearchParams(search);
    const picked = new URLSearchParams();
    for (const key of keys) {
        for (const value of current.getAll(key)) {
            picked.append(key, value);
        }
    }
    return picked.toString();
};

interface BaseLinkTab {
    type?: 'link';
    title: string;
    url: string;
    /** Query params carried over from the current URL when navigating to this tab. */
    preserveSearchParams?: readonly string[];
    Component?: JSX.Element;
    disabled?: boolean;
    hidden?: boolean;
    noPadding?: boolean;
    dataTestId?: string;
    highlightUrl?: string;
    aliases?: string[];
}

interface MenuTab {
    type: 'menu';
    title: React.ReactNode;
    items: DropdownItemConfig[];
}

interface SeparatorTab {
    type: 'separator';
}

export type RoutingTab = BaseLinkTab | MenuTab | SeparatorTab;

/** The tab panel's padding; a `noPadding` page applies these to its own rows instead. */
export const TAB_PANEL_PADDING_X = 'pr-6 pl-5';
export const TAB_PANEL_PADDING_BOTTOM = 'pb-16';

const TabPanelContext = createContext<HTMLElement | null>(null);

/** The active tab's scrolling panel (null until mounted), for sticky offsets and scroll spies. */
export const useTabPanel = () => useContext(TabPanelContext);

export interface RoutingTabsProps {
    tabs: RoutingTab[];
    baseUrl: string;
    fallback: string;
    activeColor?: string;
    children?: React.ReactNode;
}

const isLinkTab = (tab: RoutingTab): tab is BaseLinkTab => {
    return !tab.type || tab.type === 'link';
};

const isMenuTab = (tab: RoutingTab): tab is MenuTab => {
    return tab.type === 'menu';
};

const isSeparatorTab = (tab: RoutingTab): tab is SeparatorTab => {
    return tab.type === 'separator';
};

/** What the URL resolves to: a link tab or a menu item. Per-entry options (title, noPadding) are read here. */
type ActiveEntry = BaseLinkTab | DropdownItemConfig;

interface ActiveTabResult {
    activeEntry?: ActiveEntry;
    activeUrl?: string;
    normalizedBase: string;
}

export const useActiveTab = (baseUrl: string, tabs: RoutingTab[]): ActiveTabResult => {
    const location = useLocation();

    const normalizedBase = baseUrl.replace(/\/+$/, '');
    const fullPath = location.pathname;
    const relative = fullPath.startsWith(normalizedBase) ? fullPath.slice(normalizedBase.length) : '';
    const firstSegment = relative.startsWith('/') ? `/${relative.split('/')[1]}` : undefined;

    const activeTab = tabs.find((tab) => {
        if (!isLinkTab(tab)) return false;
        return !tab.disabled && (tab.url === firstSegment || tab.aliases?.includes(firstSegment as string));
    });

    if (activeTab && isLinkTab(activeTab)) {
        return { activeEntry: activeTab, activeUrl: activeTab.highlightUrl || activeTab.url, normalizedBase };
    }

    for (const tab of tabs) {
        if (isMenuTab(tab)) {
            const activeItem = tab.items.find((item) => item.url === firstSegment);
            if (activeItem) {
                return { activeEntry: activeItem, activeUrl: activeItem.url, normalizedBase };
            }
        }
    }

    return { normalizedBase };
};

interface LinkTabItemProps {
    tab: BaseLinkTab;
    baseUrl: string;
    activeColor?: string;
}

const LinkTabItem = (props: LinkTabItemProps) => {
    const location = useLocation();
    const tabValue = props.tab.url.slice(1);
    const search = props.tab.preserveSearchParams
        ? pickSearchParams(location.search, props.tab.preserveSearchParams)
        : '';

    return (
        <TabsTrigger
            value={tabValue}
            disabled={props.tab.disabled}
            data-testid={props.tab.dataTestId}
            style={
                props.activeColor
                    ? ({
                          '--active-border-color': props.activeColor,
                      } as React.CSSProperties)
                    : undefined
            }
            className={cn(
                props.activeColor &&
                    'hover:border-[var(--active-border-color)] data-[state=active]:border-[var(--active-border-color)]',
            )}
            asChild
        >
            <Link to={{ pathname: `${props.baseUrl}${props.tab.url}`, search }} replace>
                {props.tab.title}
            </Link>
        </TabsTrigger>
    );
};

interface MenuTabItemProps {
    tab: MenuTab;
    baseUrl: string;
    activeUrl?: string;
    activeColor?: string;
}

const MenuTabItem = (props: MenuTabItemProps) => {
    const navigate = useNavigate();
    const isActive = props.tab.items.some((item) => item.url === props.activeUrl);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                className={cn(
                    'inline-flex h-full cursor-pointer items-center justify-center border-b-[3px] px-3 py-1 text-center text-sm leading-tight no-underline transition-colors duration-200',
                    'focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 focus-visible:outline-none',
                    'disabled:pointer-events-none disabled:opacity-50',
                    isActive && 'border-[var(--active-border-color)] font-normal',
                    !isActive && 'border-transparent font-light text-[#5A4F4E]',
                    props.activeColor && 'hover:border-[var(--active-border-color)]',
                )}
                style={
                    props.activeColor
                        ? ({
                              '--active-border-color': props.activeColor,
                          } as React.CSSProperties)
                        : undefined
                }
            >
                {props.tab.title}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                {props.tab.items.map((item) => (
                    <DropdownMenuItem key={item.url} onClick={() => navigate(`${props.baseUrl}${item.url}`)}>
                        {item.title}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const SeparatorTabItem = () => {
    return <div className="mx-4 h-8 w-px bg-gray-300" />;
};

export const RoutingTabs = (props: RoutingTabsProps) => {
    const { activeEntry, activeUrl, normalizedBase } = useActiveTab(props.baseUrl, props.tabs);
    const [panel, setPanel] = useState<HTMLElement | null>(null);

    return (
        <Tabs value={activeUrl?.slice(1)} className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-shrink-0 overflow-x-auto [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:bg-transparent [&:hover::-webkit-scrollbar-thumb]:rounded-full [&:hover::-webkit-scrollbar-thumb]:bg-gray-300">
                <TabsList className="w-max min-w-full">
                    {props.tabs.map((tab, index) => {
                        if (isLinkTab(tab)) {
                            if (tab.hidden) return null;
                            return (
                                <LinkTabItem
                                    key={tab.title}
                                    tab={tab}
                                    baseUrl={normalizedBase}
                                    activeColor={props.activeColor}
                                />
                            );
                        }
                        if (isMenuTab(tab)) {
                            return (
                                <MenuTabItem
                                    key={tab.items.map((item) => item.url).join('-')}
                                    tab={tab}
                                    baseUrl={normalizedBase}
                                    activeUrl={activeUrl}
                                    activeColor={props.activeColor}
                                />
                            );
                        }
                        if (isSeparatorTab(tab)) {
                            return <SeparatorTabItem key={`separator-${index}`} />;
                        }
                        return null;
                    })}
                </TabsList>
            </div>
            <TabsContent
                ref={setPanel}
                value={activeUrl?.slice(1) || ''}
                className={cn(
                    'relative flex-1 overflow-y-auto',
                    activeEntry?.noPadding ? 'p-0' : cn('pt-6', TAB_PANEL_PADDING_X, TAB_PANEL_PADDING_BOTTOM),
                )}
            >
                <TabPanelContext.Provider value={panel}>
                    {/* Only link tabs carry a Component; menu items render through children. */}
                    <ErrorBoundary>
                        {props.children ??
                            (activeEntry && 'Component' in activeEntry ? activeEntry.Component : undefined)}
                    </ErrorBoundary>
                </TabPanelContext.Provider>
            </TabsContent>
        </Tabs>
    );
};
